/**
 * useTypewriter — the ONE typewriter primitive for the web app.
 *
 * A character-by-character text reveal driven by `requestAnimationFrame` (smooth
 * and scheduler-starvation-immune while the tab is visible) with a `setTimeout`
 * safety net that guarantees the full text still renders when rAF is UNAVAILABLE:
 * browsers PAUSE requestAnimationFrame on a hidden/background tab, which would
 * otherwise strand a live answer forever (root cause of the T0-bis strand,
 * instrumented on UAT 2026-07-19; setTimeout keeps firing on hidden tabs).
 *
 * This is the single implementation — AssistantPanel and /bill/explain both
 * consume it, so the safety net can never again be present on one surface and
 * missing on a sibling (F-A). Both carry their own hidden-tab integration test.
 * (A third, landing-side consumer was removed with the pre-redesign era in
 * 2026-07-27.) The hook owns ONLY the
 * text animation; citations, badges, fallback banners and think-steps stay in
 * the consumers. A source-contract test bans `requestAnimationFrame` anywhere in
 * apps/web/{app,components} except this file.
 */
import { useState, useRef, useCallback, useEffect } from "react";

export interface Typewriter {
  /** The text revealed so far (grows to the full string, or jumps to it via the safety net). */
  displayedText: string;
  /** True once the full text has rendered and both timers are cancelled. */
  isDone: boolean;
  /** Begin revealing `text`; resets any prior run. `onDone` fires exactly once at completion. */
  start: (text: string, onDone?: () => void) => void;
  /** Cancel the rAF loop AND the safety-net timer and reset to empty. Idempotent. */
  clear: () => void;
}

/** @param speedMs milliseconds per character (default 10 = ~100 chars/sec). */
export function useTypewriter(speedMs = 10): Typewriter {
  const [displayedText, setDisplayedText] = useState("");
  const [isDone, setIsDone] = useState(false);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards `onDone` to fire once even if rAF and the safety net both reach finish.
  const doneRef = useRef(false);

  const cancelTimers = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clear = useCallback(() => {
    cancelTimers();
    doneRef.current = false;
    setDisplayedText("");
    setIsDone(false);
  }, [cancelTimers]);

  const start = useCallback(
    (text: string, onDone?: () => void) => {
      cancelTimers();
      doneRef.current = false;
      setDisplayedText("");
      setIsDone(false);

      const finish = () => {
        if (doneRef.current) return;
        doneRef.current = true;
        cancelTimers();
        setDisplayedText(text);
        setIsDone(true);
        onDone?.();
      };

      const startTime = performance.now();
      const tick = (now: number) => {
        const charIndex = Math.min(Math.floor((now - startTime) / speedMs), text.length);
        setDisplayedText(text.slice(0, charIndex));
        if (charIndex >= text.length) {
          finish();
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      // rAF is authoritative when visible; the safety net lands the answer when
      // rAF is paused (hidden tab). Whichever reaches `finish` first wins; the
      // doneRef guard makes the loser a no-op.
      rafRef.current = requestAnimationFrame(tick);
      timerRef.current = setTimeout(finish, text.length * speedMs + 1200);
    },
    [cancelTimers, speedMs],
  );

  // Cancel any in-flight timers on unmount (no setState — the component is gone).
  useEffect(() => cancelTimers, [cancelTimers]);

  return { displayedText, isDone, start, clear };
}
